import React, { useCallback, useMemo, useRef, useState } from "react";
import { AxButton, AxInput, AxPassword } from "@dynata/axiom3";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import PortalRequestInvitation from "widgets/portalRequestInvitation";
import paths, { ADHOC_REPORT_PATHS } from "paths";
import { useLogin } from "hooks";

const EMPTY_PASSWORD_ERROR = "Password required";
const EMPTY_EMAIL_ERROR = "Email required";

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [passwordError, setPasswordError] = useState<string>();
  const [emailError, setEmailError] = useState<string>();
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const requestInvitatitonDialogRef = useRef<HTMLDialogElement>(null);
  const [requestAccountRequestSent, setRequestAccountRequestSent] =
    useState(false);
  const submit = (e: any) => {
    e.preventDefault();

    if (!email.trim()) {
      setEmailError(EMPTY_EMAIL_ERROR);
    }

    if (!password.trim()) {
      setPasswordError(EMPTY_PASSWORD_ERROR);
    }

    setLoading(true);

    login.mutate(
      { email: email.trim(), password: password.trim() },
      {
        onSettled: () => {
          setLoading(false);
        },
      },
    );
  };

  const contextId = useMemo(() => {
    const redirectPath = location.state?.redirect;
    if (!redirectPath) return undefined;

    for (const path of ADHOC_REPORT_PATHS) {
      const url = new URL(redirectPath);
      const matched = matchPath(
        { path, end: true, caseSensitive: false },
        url.pathname,
      );

      if (matched)
        return (
          (matched.params.contextId || url.searchParams.get("ctx")) ?? undefined
        );
    }

    return undefined;
  }, [location.state]);

  const onOpenRequestInvitationDialog = useCallback(() => {
    requestInvitatitonDialogRef.current?.showModal();
  }, []);

  const onCloseRequestInvitationDialog = useCallback(() => {
    requestInvitatitonDialogRef.current?.close();
  }, []);

  return (
    <>
      {contextId && (
        <PortalRequestInvitation
          setRequestAccountRequestSent={setRequestAccountRequestSent}
          onClose={onCloseRequestInvitationDialog}
          context_id={contextId}
          ref={requestInvitatitonDialogRef}
        />
      )}
      <form className="form-wrapper">
        <div className="login-fields">
          <AxInput
            prefix={<i className="fa-regular fa-envelope" />}
            onPressEnter={submit}
            placeholder="Email"
            value={email}
            size="large"
            onChange={(e: any) => {
              setEmail(e.target.value);
            }}
          />
          <AxPassword
            prefix={<i className="fa-regular fa-key" />}
            placeholder="Password"
            onPressEnter={submit}
            value={password}
            size="large"
            onChange={(e: any) => {
              setPassword(e.target.value);
            }}
          />
          <AxButton
            // TODO: replace for <Link/>
            type="link"
            onClick={() => navigate(`/${paths.open.RESET}`)}
            size="large"
            color="main"
          >
            Forgot your password?
          </AxButton>
        </div>
        <div className="login-actions">
          <AxButton
            disabled={
              !password.trim() || !email.trim() || emailError || passwordError
                ? "disabled"
                : ""
            }
            loading={loading}
            onClick={submit}
            size="large"
          >
            Log In
          </AxButton>
          {contextId && (
            <div
              className="body semibold l"
              style={{
                display: "inline-flex",
                alignItems: "baseline",
                justifyContent: "center",
              }}
            >
              {requestAccountRequestSent ? (
                "Access requested"
              ) : (
                <>
                  {" "}
                  Don't have an account?&nbsp;
                  <AxButton
                    type="link"
                    size="large"
                    color="main"
                    onClick={onOpenRequestInvitationDialog}
                  >
                    Request access
                  </AxButton>
                </>
              )}
            </div>
          )}
        </div>
      </form>
    </>
  );
};
